import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Notification, UnreadCountResponse } from "@/lib/types";

/**
 * Fetch notifications for the current user.
 * Only works for admins and super-admins.
 */
export function useNotifications(limit = 50, unreadOnly = false, enabled = true) {
  return useQuery({
    queryKey: ["notifications", { limit, unreadOnly }],
    queryFn: async () => {
      const res = await api.get<Notification[]>("/notifications", {
        params: { limit, unread_only: unreadOnly },
      });
      return res.data;
    },
    // Only admins/super-admins may read notifications — don't poll (and 403) for
    // everyone else.
    enabled,
    refetchInterval: 30000,
    retry: false,
  });
}

/**
 * Fetch unread notification count.
 */
export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const res = await api.get<UnreadCountResponse>("/notifications/unread-count");
      return res.data.count;
    },
    enabled,
    refetchInterval: 15000,
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * Mark a notification as read.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await api.patch(`/notifications/${notificationId}/read`, { is_read: true });
    },
    onSuccess: () => {
      // Invalidate to refresh the list and count
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/**
 * Mark all notifications as read.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post("/notifications/mark-all-read");
    },
    onSuccess: () => {
      // Invalidate to refresh the list and count
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
